import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import PendingActionsIcon from '@mui/icons-material/PendingActionsOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmptyOutlined';
import { apiClient } from '@/api/axiosClient';
import ChartCard from '@/components/dashboard/ChartCard';
import DashboardBarChart from '@/components/dashboard/DashboardBarChart';
import DashboardPieChart from '@/components/dashboard/DashboardPieChart';
import TvKpiTile from '@/components/tvDashboard/TvKpiTile';
import TvTopList from '@/components/tvDashboard/TvTopList';
import { useCategoryColors } from '@/pages/dashboard/dashboardColors';
import { average, groupCountBy, topN } from '@/lib/dashboardAggregations';
import type { ApprovalTurnaroundRow, PendingApprovalAgingRow } from '@/types/reports';

const STATUS_SLICE_COLORS = ['#4edea3', '#ba1a1a', '#ffb951', '#0566d9'];

export default function ApprovalsSlide() {
  const { t } = useTranslation('dashboard');
  const colors = useCategoryColors();

  const { data: pending = [] } = useQuery({
    queryKey: ['reports', 'pending-approvals-aging'],
    queryFn: async () => (await apiClient.get<PendingApprovalAgingRow[]>('/reports/pending-approvals-aging')).data,
    refetchInterval: 60_000,
  });
  const { data: turnaround = [] } = useQuery({
    queryKey: ['reports', 'approval-turnaround'],
    queryFn: async () => (await apiClient.get<ApprovalTurnaroundRow[]>('/reports/approval-turnaround')).data,
    refetchInterval: 60_000,
  });

  const overdueCount = useMemo(() => pending.filter((r) => r.isOverdue).length, [pending]);
  const avgTurnaround = useMemo(() => average(turnaround.map((r) => r.turnaroundHours).filter((v): v is number => v != null)), [turnaround]);
  const avgPendingAge = useMemo(() => average(pending.map((r) => r.hoursPending)), [pending]);

  const byTransactionType = useMemo(() => groupCountBy(pending, (r) => r.transactionType), [pending]);
  const decisionSplit = useMemo(() => groupCountBy(turnaround, (r) => r.status), [turnaround]);
  const mostOverdue = useMemo(() => topN(pending, (r) => r.hoursPending, 5), [pending]);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 2, py: 1 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2 }}>
        <TvKpiTile label={t('approvals.pendingCount')} value={pending.length} icon={<PendingActionsIcon />} accentColor={colors.approvals} />
        <TvKpiTile label={t('approvals.overdueCount')} value={overdueCount} icon={<ReportProblemOutlinedIcon />} accentColor={colors.adjustments} />
        <TvKpiTile label={t('approvals.avgTurnaround')} value={avgTurnaround > 0 ? `${avgTurnaround.toFixed(1)}h` : '—'} icon={<TimerOutlinedIcon />} accentColor={colors.approvals} />
        <TvKpiTile label={t('approvals.avgPendingAge')} value={avgPendingAge > 0 ? `${avgPendingAge.toFixed(1)}h` : '—'} icon={<HourglassEmptyIcon />} accentColor={colors.approvals} />
      </Box>
      <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: '1.1fr 0.8fr 0.9fr', gap: 2, minHeight: 0 }}>
        <ChartCard title={t('approvals.byTypeTitle')} height={420}>
          <DashboardBarChart
            categories={byTransactionType.map((r) => r.key)}
            series={[{ label: t('approvals.pendingCount'), data: byTransactionType.map((r) => r.count), color: colors.approvals }]}
            height={420}
            legendPosition="bottom"
          />
        </ChartCard>
        <ChartCard title={t('approvals.decisionSplitTitle')} height={420}>
          <DashboardPieChart
            data={decisionSplit.map((s, i) => ({ label: s.key, value: s.count, color: STATUS_SLICE_COLORS[i % STATUS_SLICE_COLORS.length] }))}
            height={420}
            legendPosition="bottom"
          />
        </ChartCard>
        <TvTopList
          title={t('approvals.mostOverdueTitle')}
          accentColor={colors.adjustments}
          items={mostOverdue.map((r) => ({ label: `${r.transactionType} (L${r.currentLevelNo})`, value: r.hoursPending, formattedValue: `${r.hoursPending.toFixed(0)}h` }))}
        />
      </Box>
    </Box>
  );
}
